import React, { useEffect, useMemo, useState } from "react";
import { useRbac } from "../../context";
import { createHunarAllocation, createHunarInputSheet, deleteHunarInputSheet, deleteHunarSheetJobMapping, getHunarAllocations, getHunarInputSheets, getHunarSheetIngestTracking, updateHunarAllocation, updateHunarInputSheet, updateHunarSheetJobMapping } from "../../services/hunarAllocationApi";
import { getAllUsers } from "../../services/usersapi";
import { getJobsForSelection } from "../../services/jobsapi";
import { SearchableSelect } from "../../components/ui/searchable-select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";

type Allocation = {
  _id: string;
  hunarAgentId: string;
  status: "active" | "inactive";
  dailyCap?: number;
  internalJobId?: { _id?: string; title?: string; location?: string; jobId?: string } | null;
  assignedRecruiterId?: { _id?: string; username?: string; email?: string; role?: string } | null;
  sheetJobMappings?: Array<{ _id: string; sheetJobId: string; status: "active" | "inactive" }>;
};

type IngestStats = {
  rowsRead: number;
  missingNameOrMobile: number;
  alreadyExists: number;
  queued: number;
  skippedUnmatchedJobId: number;
  skippedAlreadyApproached: number;
  skippedDailyCap: number;
  errors: number;
};

type InputSheet = {
  _id: string;
  spreadsheetId: string;
  spreadsheetUrl: string;
  sheetName: string;
  label?: string | null;
  columnMap?: {
    candidateName?: string | null;
    mobileNumber?: string | null;
    jobId?: string | null;
    email?: string | null;
  } | null;
  status: "active" | "inactive";
  lastIngestAt?: string | null;
  lastIngestError?: string | null;
  lastIngestStats?: IngestStats | null;
};

type SheetIngestTracking = {
  sheets: InputSheet[];
  today: {
    queued: number;
    cancelledAlreadyApproached: number;
    cancelledDailyCap: number;
    callPipeline: number;
    notConnected: number;
    completed: number;
    failed: number;
    other: number;
    total: number;
  };
  allTimeByStatus: Record<string, number>;
};

const DEFAULT_COLUMN_MAP = {
  candidateName: "Candidate Name",
  mobileNumber: "Mobile Number",
  jobId: "Job ID",
  email: "Email",
};

const recruiterKey = (allocation: Allocation) =>
  allocation.assignedRecruiterId?._id || "unassigned";

const recruiterLabel = (allocation: Allocation) =>
  allocation.assignedRecruiterId?.username || allocation.assignedRecruiterId?.email || "Unassigned recruiter";

const HunarAllocationsPage: React.FC = () => {
  const { user } = useRbac();
  const isAdmin = user?.role === "ADMIN";
  const isRecruiter = !isAdmin;
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [jobs, setJobs] = useState<Array<{ _id: string; title: string; location?: string; companyName?: string; jobId?: string }>>([]);
  const [recruiters, setRecruiters] = useState<Array<{ id: string; username: string; email: string; role: string }>>([]);
  const [newAllocation, setNewAllocation] = useState({
    internalJobId: "",
    hunarAgentId: "",
    assignedRecruiterId: "",
  });
  const [capInputs, setCapInputs] = useState<Record<string, string>>({});
  const [inputSheets, setInputSheets] = useState<InputSheet[]>([]);
  const [newSheet, setNewSheet] = useState({
    spreadsheetUrl: "",
    sheetName: "",
    label: "",
    columnMap: { ...DEFAULT_COLUMN_MAP },
  });
  const [sheetColumnDrafts, setSheetColumnDrafts] = useState<
    Record<string, { candidateName: string; mobileNumber: string; jobId: string; email: string }>
  >({});
  const [savingSheetColumnsId, setSavingSheetColumnsId] = useState<string | null>(null);
  const [sheetToDelete, setSheetToDelete] = useState<InputSheet | null>(null);
  const [deletingSheet, setDeletingSheet] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<{ _id: string; sheetJobId: string } | null>(null);
  const [deletingMapping, setDeletingMapping] = useState(false);
  const [ingestTracking, setIngestTracking] = useState<SheetIngestTracking | null>(null);

  const loadAllocations = async () => {
    try {
      const response = await getHunarAllocations();
      setAllocations(response?.data?.allocations || []);
    } catch {
      // Toast is shown by the API client.
    }
  };

  const loadInputSheets = async () => {
    if (!isAdmin) return;
    try {
      const response = await getHunarInputSheets();
      const sheets: InputSheet[] = response?.data?.sheets || [];
      setInputSheets(sheets);
      setSheetColumnDrafts(
        Object.fromEntries(
          sheets.map((sheet) => [
            sheet._id,
            {
              candidateName: sheet.columnMap?.candidateName || DEFAULT_COLUMN_MAP.candidateName,
              mobileNumber: sheet.columnMap?.mobileNumber || DEFAULT_COLUMN_MAP.mobileNumber,
              jobId: sheet.columnMap?.jobId || DEFAULT_COLUMN_MAP.jobId,
              email: sheet.columnMap?.email || DEFAULT_COLUMN_MAP.email,
            },
          ])
        )
      );
    } catch {
      // Toast is shown by the API client.
    }
  };

  const loadIngestTracking = async () => {
    if (!isAdmin) return;
    try {
      const response = await getHunarSheetIngestTracking();
      setIngestTracking(response?.data || null);
    } catch {
      // Toast is shown by the API client.
    }
  };

  useEffect(() => {
    loadAllocations();
  }, []);

  useEffect(() => {
    loadInputSheets();
    loadIngestTracking();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const [jobsResponse, usersResponse] = await Promise.all([
          getJobsForSelection({ _t: Date.now() }),
          getAllUsers({ limit: "3000", role: ["HR", "HM", "SHM"] }),
        ]);
        if (cancelled) return;
        setJobs(
          (jobsResponse?.data?.jobs || [])
            .map((job: any) => ({
              _id: String(job._id || job.id || ""),
              title: job.title || "Untitled job",
              location: job.location,
              companyName: job.companyName,
              jobId: job.jobId,
            }))
            .filter((job: { _id: string }) => Boolean(job._id))
        );
        setRecruiters(
          (usersResponse?.data?.users || [])
            .map((recruiter: any) => ({
              id: String(recruiter.id || recruiter._id || ""),
              username: recruiter.username || recruiter.email || "User",
              email: recruiter.email || "",
              role: recruiter.role || "",
            }))
            .filter((recruiter: { id: string }) => Boolean(recruiter.id))
        );
      } catch {
        // Toast is shown by the API client; keep page usable with empty selects.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const handleCreateAllocation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createHunarAllocation(newAllocation);
      setNewAllocation({ internalJobId: "", hunarAgentId: "", assignedRecruiterId: "" });
      await loadAllocations();
    } catch {
      // Toast is shown by the API client.
    }
  };

  const recruiterGroups = useMemo(() => {
    const grouped = new Map<string, Allocation[]>();
    for (const allocation of allocations) {
      const key = recruiterKey(allocation);
      const list = grouped.get(key) || [];
      list.push(allocation);
      grouped.set(key, list);
    }
    return Array.from(grouped.entries()).map(([key, rows]) => ({
      key,
      recruiterName: recruiterLabel(rows[0]),
      dailyCap: Number(rows[0]?.dailyCap ?? 0),
      allocationIdForCap: rows[0]?._id,
      rows,
    }));
  }, [allocations]);

  const saveRecruiterCap = async (allocationId: string, recruiterId: string, value: string, fallback: number) => {
    try {
      await updateHunarAllocation(allocationId, {
        dailyCap: Number(value || fallback || 0),
      });
      setCapInputs((prev) => {
        const next = { ...prev };
        delete next[recruiterId];
        return next;
      });
      await loadAllocations();
    } catch {
      // Toast is shown by the API client.
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">AI Allocations</h1>
        <p className="text-sm text-gray-600">
          {isAdmin
            ? "Assign AI agents to jobs and add Google Sheets. Recruiters add job IDs from the dashboard job listing."
            : "Job IDs are added from the dashboard job listing. Existing IDs for this allocation are shown below."}
        </p>
      </div>

      {isAdmin && (
        <div className="bg-white p-4 rounded-lg border space-y-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Input Google Sheets</h2>
            <p className="text-sm text-gray-600">
              Add sheets and map the exact column header names used for Candidate Name, Mobile, Job ID, and Email.
            </p>
          </div>
          <form
            className="space-y-3"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createHunarInputSheet({
                  spreadsheetUrl: newSheet.spreadsheetUrl,
                  sheetName: newSheet.sheetName,
                  label: newSheet.label || undefined,
                  columnMap: newSheet.columnMap,
                });
                setNewSheet({
                  spreadsheetUrl: "",
                  sheetName: "",
                  label: "",
                  columnMap: { ...DEFAULT_COLUMN_MAP },
                });
                await loadInputSheets();
              } catch {
                // Toast is shown by the API client.
              }
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                className="border rounded-md px-3 py-2 md:col-span-2"
                placeholder="Google Sheet link"
                value={newSheet.spreadsheetUrl}
                onChange={(e) => setNewSheet((prev) => ({ ...prev, spreadsheetUrl: e.target.value }))}
                required
              />
              <input
                className="border rounded-md px-3 py-2"
                placeholder="Tab name"
                value={newSheet.sheetName}
                onChange={(e) => setNewSheet((prev) => ({ ...prev, sheetName: e.target.value }))}
                required
              />
              <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded-md">
                Add sheet
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                className="border rounded-md px-3 py-2"
                placeholder="Candidate Name column"
                value={newSheet.columnMap.candidateName}
                onChange={(e) =>
                  setNewSheet((prev) => ({
                    ...prev,
                    columnMap: { ...prev.columnMap, candidateName: e.target.value },
                  }))
                }
                required
              />
              <input
                className="border rounded-md px-3 py-2"
                placeholder="Mobile Number column"
                value={newSheet.columnMap.mobileNumber}
                onChange={(e) =>
                  setNewSheet((prev) => ({
                    ...prev,
                    columnMap: { ...prev.columnMap, mobileNumber: e.target.value },
                  }))
                }
                required
              />
              <input
                className="border rounded-md px-3 py-2"
                placeholder="Job ID column"
                value={newSheet.columnMap.jobId}
                onChange={(e) =>
                  setNewSheet((prev) => ({
                    ...prev,
                    columnMap: { ...prev.columnMap, jobId: e.target.value },
                  }))
                }
                required
              />
              <input
                className="border rounded-md px-3 py-2"
                placeholder="Email column"
                value={newSheet.columnMap.email}
                onChange={(e) =>
                  setNewSheet((prev) => ({
                    ...prev,
                    columnMap: { ...prev.columnMap, email: e.target.value },
                  }))
                }
              />
            </div>
          </form>
          {inputSheets.length === 0 ? (
            <div className="text-sm text-gray-500">No input sheets yet.</div>
          ) : (
            <div className="space-y-3">
              {inputSheets.map((sheet) => {
                const draft = sheetColumnDrafts[sheet._id] || DEFAULT_COLUMN_MAP;
                return (
                  <div key={sheet._id} className="border rounded-md px-3 py-3 space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="min-w-0">
                        <a
                          href={sheet.spreadsheetUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-blue-700 truncate block"
                        >
                          {sheet.label || sheet.sheetName}
                        </a>
                        <div className="text-xs text-gray-600">
                          Tab: {sheet.sheetName}
                          {sheet.lastIngestAt ? ` · Last ingest ${new Date(sheet.lastIngestAt).toLocaleString("en-IN")}` : ""}
                        </div>
                        {sheet.lastIngestError ? (
                          <div className="text-xs text-red-600">{sheet.lastIngestError}</div>
                        ) : null}
                        {sheet.lastIngestStats ? (
                          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                            <span>Read {sheet.lastIngestStats.rowsRead}</span>
                            <span>Queued {sheet.lastIngestStats.queued}</span>
                            <span>Unmatched job {sheet.lastIngestStats.skippedUnmatchedJobId}</span>
                            <span>Already approached {sheet.lastIngestStats.skippedAlreadyApproached}</span>
                            <span>Daily cap {sheet.lastIngestStats.skippedDailyCap}</span>
                            <span>Existing {sheet.lastIngestStats.alreadyExists}</span>
                            <span>Missing fields {sheet.lastIngestStats.missingNameOrMobile}</span>
                            <span>Errors {sheet.lastIngestStats.errors}</span>
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${sheet.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {sheet.status}
                        </span>
                        <button
                          type="button"
                          className="px-3 py-2 border rounded-md text-sm"
                          onClick={async () => {
                            try {
                              await updateHunarInputSheet(sheet._id, {
                                status: sheet.status === "active" ? "inactive" : "active",
                              });
                              await loadInputSheets();
                            } catch {
                              // Toast is shown by the API client.
                            }
                          }}
                        >
                          Toggle Status
                        </button>
                        <button
                          type="button"
                          className="px-3 py-2 border border-red-200 text-red-700 rounded-md text-sm"
                          onClick={() => setSheetToDelete(sheet)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                      <input
                        className="border rounded-md px-3 py-2 text-sm"
                        placeholder="Candidate Name column"
                        value={draft.candidateName}
                        onChange={(e) =>
                          setSheetColumnDrafts((prev) => ({
                            ...prev,
                            [sheet._id]: { ...draft, candidateName: e.target.value },
                          }))
                        }
                      />
                      <input
                        className="border rounded-md px-3 py-2 text-sm"
                        placeholder="Mobile Number column"
                        value={draft.mobileNumber}
                        onChange={(e) =>
                          setSheetColumnDrafts((prev) => ({
                            ...prev,
                            [sheet._id]: { ...draft, mobileNumber: e.target.value },
                          }))
                        }
                      />
                      <input
                        className="border rounded-md px-3 py-2 text-sm"
                        placeholder="Job ID column"
                        value={draft.jobId}
                        onChange={(e) =>
                          setSheetColumnDrafts((prev) => ({
                            ...prev,
                            [sheet._id]: { ...draft, jobId: e.target.value },
                          }))
                        }
                      />
                      <input
                        className="border rounded-md px-3 py-2 text-sm"
                        placeholder="Email column"
                        value={draft.email}
                        onChange={(e) =>
                          setSheetColumnDrafts((prev) => ({
                            ...prev,
                            [sheet._id]: { ...draft, email: e.target.value },
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="px-3 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
                        disabled={savingSheetColumnsId === sheet._id}
                        onClick={async () => {
                          try {
                            setSavingSheetColumnsId(sheet._id);
                            await updateHunarInputSheet(sheet._id, { columnMap: draft });
                            await loadInputSheets();
                          } catch {
                            // Toast is shown by the API client.
                          } finally {
                            setSavingSheetColumnsId(null);
                          }
                        }}
                      >
                        {savingSheetColumnsId === sheet._id ? "Saving..." : "Save fields"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="bg-white p-4 rounded-lg border space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Sheet ingest tracking</h2>
              <p className="text-sm text-gray-600">
                Last poll breakdown per sheet, plus today&apos;s sheet Funnel numbers (IST). Unmatched Job IDs are not added to Funnel.
              </p>
            </div>
            <button
              type="button"
              className="px-3 py-2 border rounded-md text-sm self-start"
              onClick={() => loadIngestTracking()}
            >
              Refresh
            </button>
          </div>

          {ingestTracking?.today ? (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {[
                { label: "Today total", value: ingestTracking.today.total },
                { label: "Waiting 10h", value: ingestTracking.today.queued },
                { label: "Already approached", value: ingestTracking.today.cancelledAlreadyApproached },
                { label: "Daily cap skip", value: ingestTracking.today.cancelledDailyCap },
                { label: "In call pipeline", value: ingestTracking.today.callPipeline },
                { label: "Completed", value: ingestTracking.today.completed },
                { label: "Not connected", value: ingestTracking.today.notConnected },
                { label: "Failed", value: ingestTracking.today.failed },
              ].map((card) => (
                <div key={card.label} className="rounded-md border bg-slate-50 px-3 py-2">
                  <div className="text-xs text-gray-500">{card.label}</div>
                  <div className="text-xl font-semibold text-gray-900">{card.value}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No tracking data yet. Wait for the next sheet poll.</div>
          )}

          {(ingestTracking?.sheets || []).length > 0 ? (
            <div className="overflow-x-auto border rounded-md">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Sheet</th>
                    <th className="px-3 py-2 font-medium">Last ingest</th>
                    <th className="px-3 py-2 font-medium">Read</th>
                    <th className="px-3 py-2 font-medium">Queued</th>
                    <th className="px-3 py-2 font-medium">Unmatched job</th>
                    <th className="px-3 py-2 font-medium">Approached</th>
                    <th className="px-3 py-2 font-medium">Daily cap</th>
                    <th className="px-3 py-2 font-medium">Existing</th>
                    <th className="px-3 py-2 font-medium">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {ingestTracking!.sheets.map((sheet) => {
                    const stats = sheet.lastIngestStats;
                    return (
                      <tr key={sheet._id} className="border-t">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{sheet.label || sheet.sheetName}</div>
                          <div className="text-xs text-gray-500">Tab: {sheet.sheetName}</div>
                          {sheet.lastIngestError ? (
                            <div className="text-xs text-red-600 mt-0.5">{sheet.lastIngestError}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {sheet.lastIngestAt ? new Date(sheet.lastIngestAt).toLocaleString("en-IN") : "—"}
                        </td>
                        <td className="px-3 py-2">{stats?.rowsRead ?? "—"}</td>
                        <td className="px-3 py-2">{stats?.queued ?? "—"}</td>
                        <td className="px-3 py-2">{stats?.skippedUnmatchedJobId ?? "—"}</td>
                        <td className="px-3 py-2">{stats?.skippedAlreadyApproached ?? "—"}</td>
                        <td className="px-3 py-2">{stats?.skippedDailyCap ?? "—"}</td>
                        <td className="px-3 py-2">{stats?.alreadyExists ?? "—"}</td>
                        <td className="px-3 py-2">{stats?.errors ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}

      {isAdmin && (
        <form onSubmit={handleCreateAllocation} className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-white p-4 rounded-lg border">
          <SearchableSelect
            options={jobs.map((job) => ({
              value: job._id,
              label: (
                <div className="min-w-0">
                  <div className="truncate text-left">
                    {job.title}
                    {job.location ? ` - ${job.location}` : ""}
                  </div>
                  <div className="truncate text-left text-xs text-gray-500">
                    {[job.jobId, job.companyName].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ),
              searchText: `${job.jobId || ""} ${job.title} ${job.location || ""} ${job.companyName || ""}`.trim(),
            }))}
            value={newAllocation.internalJobId}
            onValueChange={(value) => setNewAllocation((prev) => ({ ...prev, internalJobId: value }))}
            placeholder="Select job"
            className="w-full"
          />
          <input
            className="border rounded-md px-3 py-2"
            placeholder="AI agent ID"
            value={newAllocation.hunarAgentId}
            onChange={(e) => setNewAllocation((prev) => ({ ...prev, hunarAgentId: e.target.value }))}
            required
          />
          <SearchableSelect
            options={recruiters.map((recruiter) => ({
              value: recruiter.id,
              label: `${recruiter.username} (${recruiter.role})`,
              searchText: `${recruiter.username} ${recruiter.email} ${recruiter.role}`.trim(),
            }))}
            value={newAllocation.assignedRecruiterId}
            onValueChange={(value) => setNewAllocation((prev) => ({ ...prev, assignedRecruiterId: value }))}
            placeholder="Select recruiter"
            className="w-full"
          />
          <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded-md">
            Create Allocation
          </button>
        </form>
      )}

      <div className="space-y-6">
        {recruiterGroups.length === 0 ? (
          <div className="bg-white border rounded-lg p-6 text-sm text-gray-500">No AI allocations found.</div>
        ) : (
          recruiterGroups.map((group) => (
            <div key={group.key} className="space-y-3">
              <div className="bg-slate-50 border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-900">{group.recruiterName}</div>
                  <div className="text-sm text-gray-600">
                    Daily Funnel cap for this recruiter (IST), shared across Approaches + Sheet and {group.rows.length} job{group.rows.length === 1 ? "" : "s"}
                  </div>
                </div>
                {isAdmin && group.allocationIdForCap ? (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 whitespace-nowrap">Daily cap</label>
                    <input
                      type="number"
                      min={0}
                      className="w-24 border rounded-md px-2 py-1 text-sm bg-white"
                      value={capInputs[group.key] ?? String(group.dailyCap)}
                      onChange={(e) =>
                        setCapInputs((prev) => ({ ...prev, [group.key]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      className="px-3 py-2 border rounded-md text-sm bg-white"
                      onClick={() =>
                        saveRecruiterCap(
                          group.allocationIdForCap!,
                          group.key,
                          capInputs[group.key] ?? String(group.dailyCap),
                          group.dailyCap
                        )
                      }
                    >
                      Save cap
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">Daily cap: {group.dailyCap}</div>
                )}
              </div>

              {group.rows.map((allocation) => (
                <div key={allocation._id} className="bg-white border rounded-lg p-4 space-y-4 ml-0 md:ml-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900">
                        {allocation.internalJobId?.title || "Unknown job"}
                        {allocation.internalJobId?.location ? `, ${allocation.internalJobId.location}` : ""}
                      </div>
                      <div className="text-sm text-gray-600">
                        Job ID: {allocation.internalJobId?.jobId || "—"} · Agent: {allocation.hunarAgentId}
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${allocation.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                        {allocation.status}
                      </span>
                      {isAdmin && (
                        <button
                          className="px-3 py-2 border rounded-md text-sm"
                          onClick={async () => {
                            try {
                              await updateHunarAllocation(allocation._id, {
                                status: allocation.status === "active" ? "inactive" : "active",
                              });
                              await loadAllocations();
                            } catch {
                              // Toast is shown by the API client.
                            }
                          }}
                        >
                          Toggle Status
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Sheet Job IDs</div>
                    <p className="text-xs text-gray-500">
                      {isRecruiter
                        ? "Add this job ID from Dashboard → job card → Add Job ID. Click a chip to toggle active/inactive, or × to remove."
                        : "Recruiters add job IDs from the dashboard job listing. Click a chip to toggle active/inactive, or × to remove."}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(allocation.sheetJobMappings || [])
                        .filter((mapping) => mapping?._id && mapping.sheetJobId)
                        .map((mapping) => (
                        <div
                          key={mapping._id}
                          className={`inline-flex items-center rounded-full border text-sm ${
                            mapping.status === "active"
                              ? "bg-orange-50 text-orange-700 border-orange-200"
                              : "bg-gray-50 text-gray-500 border-gray-200"
                          }`}
                        >
                          <button
                            type="button"
                            className="px-3 py-1"
                            title={mapping.status === "active" ? "Click to deactivate" : "Click to activate"}
                            onClick={async () => {
                              try {
                                await updateHunarSheetJobMapping(
                                  mapping._id,
                                  mapping.status === "active" ? "inactive" : "active"
                                );
                                await loadAllocations();
                              } catch {
                                // Toast is shown by the API client.
                              }
                            }}
                          >
                            {mapping.sheetJobId}
                          </button>
                          <button
                            type="button"
                            className="pr-2 pl-0.5 py-1 text-base leading-none opacity-70 hover:opacity-100"
                            title="Remove job ID"
                            aria-label={`Remove ${mapping.sheetJobId}`}
                            onClick={() => setMappingToDelete({ _id: mapping._id, sheetJobId: mapping.sheetJobId })}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <AlertDialog
        open={!!sheetToDelete}
        onOpenChange={(open) => {
          if (!open && !deletingSheet) setSheetToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete input sheet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{sheetToDelete?.label || sheetToDelete?.sheetName}</strong>
              {sheetToDelete?.sheetName ? ` (tab: ${sheetToDelete.sheetName})` : ""}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingSheet}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingSheet}
              onClick={async (e) => {
                e.preventDefault();
                if (!sheetToDelete) return;
                try {
                  setDeletingSheet(true);
                  await deleteHunarInputSheet(sheetToDelete._id);
                  setSheetToDelete(null);
                  await loadInputSheets();
                } catch {
                  // Toast is shown by the API client.
                } finally {
                  setDeletingSheet(false);
                }
              }}
            >
              {deletingSheet ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!mappingToDelete}
        onOpenChange={(open) => {
          if (!open && !deletingMapping) setMappingToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove sheet job ID</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{mappingToDelete?.sheetJobId}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingMapping}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingMapping}
              onClick={async (e) => {
                e.preventDefault();
                if (!mappingToDelete) return;
                try {
                  setDeletingMapping(true);
                  await deleteHunarSheetJobMapping(mappingToDelete._id);
                  setMappingToDelete(null);
                  await loadAllocations();
                } catch {
                  // Toast is shown by the API client.
                } finally {
                  setDeletingMapping(false);
                }
              }}
            >
              {deletingMapping ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HunarAllocationsPage;
